/**
 * SQLite WASM layer — manages the in-memory sql.js database with
 * IndexedDB persistence. Replaces Dexie as the local storage engine.
 *
 * Architecture:
 *  - sql.js loads SQLite compiled to WASM, runs entirely in-memory
 *  - On init: deserialize from IndexedDB (if exists) or create fresh
 *  - On writes: debounced persist to IndexedDB (500ms)
 *  - On page unload: synchronous persist via beforeunload
 *  - On significant operations (sync complete): immediate persist
 *
 * The reactive layer (see reactive.ts) provides React hooks that
 * auto-refresh when tables change, replacing Dexie's useLiveQuery.
 */

import initSqlJs, { type Database } from "sql.js";
import sqlWasmUrl from "sql.js/dist/sql-wasm.wasm?url";
import { projectNoteAttachments } from "./attachmentProjection";
import { registerSearchFunctions } from "./searchSql";

// ── Singleton ──────────────────────────────────────────────────────────────

let _db: Database | null = null;
let _initPromise: Promise<Database> | null = null;
const IDB_NAME = "caliu-sqlite";
const IDB_STORE = "database";
const IDB_KEY = "main";

// ── Change notification system ─────────────────────────────────────────────
// Components subscribe to table changes via reactive.ts hooks.

type TableChangeListener = (tables: Set<string>) => void;
const _listeners = new Set<TableChangeListener>();

export function subscribeToChanges(listener: TableChangeListener): () => void {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

/** Notify all listeners that the given tables have changed. */
export function notifyChanges(tables: string | string[]): void {
  const set = new Set(Array.isArray(tables) ? tables : [tables]);
  for (const listener of _listeners) {
    try {
      listener(set);
    } catch {
      // Don't let a broken listener crash the notification chain
    }
  }
}

// ── IndexedDB Persistence ──────────────────────────────────────────────────

function openIDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(IDB_NAME, 1);
    request.onupgradeneeded = () => {
      const idb = request.result;
      if (!idb.objectStoreNames.contains(IDB_STORE)) {
        idb.createObjectStore(IDB_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function loadFromIDB(): Promise<Uint8Array | null> {
  try {
    const idb = await openIDB();
    return new Promise((resolve, reject) => {
      const tx = idb.transaction(IDB_STORE, "readonly");
      const store = tx.objectStore(IDB_STORE);
      const req = store.get(IDB_KEY);
      req.onsuccess = () => resolve(req.result as Uint8Array | null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

async function saveToIDB(data: Uint8Array): Promise<void> {
  try {
    const idb = await openIDB();
    return new Promise((resolve, reject) => {
      const tx = idb.transaction(IDB_STORE, "readwrite");
      const store = tx.objectStore(IDB_STORE);
      const req = store.put(data, IDB_KEY);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    console.error("[sqlite] Failed to persist to IndexedDB:", e);
  }
}

// ── Debounced persist ──────────────────────────────────────────────────────

let _persistTimer: ReturnType<typeof setTimeout> | null = null;

function schedulePersist(): void {
  if (_persistTimer) clearTimeout(_persistTimer);
  _persistTimer = setTimeout(() => {
    _persistTimer = null;
    persist();
  }, 500);
}

/** Immediately persist the database to IndexedDB. */
export async function persist(): Promise<void> {
  if (!_db) return;
  if (_persistTimer) {
    clearTimeout(_persistTimer);
    _persistTimer = null;
  }
  const data = exportDatabase(_db);
  await saveToIDB(data);
}

/**
 * `db.export()`, which closes and reopens the database: that drops the
 * functions added to it, so this adds them back.
 */
export function exportDatabase(db: Database): Uint8Array {
  const data = db.export();
  registerSearchFunctions(db);
  return data;
}

// ── Schema ─────────────────────────────────────────────────────────────────

const SCHEMA_SQL = `
-- Notes table (matches D1 schema)
CREATE TABLE IF NOT EXISTS notes (
  id TEXT PRIMARY KEY,
  user_id TEXT NOT NULL DEFAULT '',
  title TEXT NOT NULL DEFAULT '',
  content TEXT NOT NULL DEFAULT '',
  is_pinned INTEGER NOT NULL DEFAULT 0,
  is_encrypted INTEGER NOT NULL DEFAULT 0,
  encryption_hint TEXT,
  word_count INTEGER NOT NULL DEFAULT 0,
  created_at TEXT NOT NULL DEFAULT (datetime('now')),
  updated_at TEXT NOT NULL DEFAULT (datetime('now')),
  deleted_at TEXT,
  archived_at TEXT,
  shared_at TEXT,
  sort_order REAL,
  base_content TEXT,
  is_read_only INTEGER NOT NULL DEFAULT 0,
  sync_status TEXT NOT NULL DEFAULT 'synced',
  -- Content sync engine (PLAN.md "Client engine"); see lib/noteEngineState.ts.
  rev INTEGER NOT NULL DEFAULT 0,
  base_updated_at TEXT NOT NULL DEFAULT '',
  inflight_client_id TEXT,
  inflight_seq INTEGER,
  inflight_base_rev INTEGER,
  inflight_ops TEXT,
  pending_ops TEXT,
  -- Sharing (PLAN.md "Sharing data", Sync): set from pulled rows of shared
  -- notes. owner_id NULL = this account's own unshared note.
  owner_id TEXT,
  owner_name TEXT,
  share_role TEXT,
  member_count INTEGER NOT NULL DEFAULT 0,
  link_access TEXT
);

CREATE INDEX IF NOT EXISTS idx_notes_updated_at ON notes(updated_at DESC);
CREATE INDEX IF NOT EXISTS idx_notes_deleted_at ON notes(deleted_at);
-- NOTE: the archived_at index is created by the v11 migration, NOT here.
-- SCHEMA_SQL runs before migrations; on an existing pre-v11 DB the notes table
-- already exists (CREATE TABLE IF NOT EXISTS is a no-op) so archived_at isn't a
-- column yet, and indexing it here would throw and block startup for upgraders.

-- Tags table (matches D1 schema)
CREATE TABLE IF NOT EXISTS tags (
  id TEXT PRIMARY KEY,
  user_id TEXT NOT NULL DEFAULT '',
  name TEXT NOT NULL,
  parent_id TEXT REFERENCES tags(id) ON DELETE SET NULL,
  color TEXT,
  icon TEXT,
  is_pinned INTEGER NOT NULL DEFAULT 0,
  created_at TEXT NOT NULL DEFAULT (datetime('now')),
  sync_status TEXT NOT NULL DEFAULT 'synced'
);

CREATE INDEX IF NOT EXISTS idx_tags_name ON tags(name);

-- Note-Tag associations (replaces tags[] array in Dexie)
CREATE TABLE IF NOT EXISTS note_tags (
  note_id TEXT NOT NULL REFERENCES notes(id) ON DELETE CASCADE,
  tag_id TEXT NOT NULL REFERENCES tags(id) ON DELETE CASCADE,
  PRIMARY KEY (note_id, tag_id)
);

CREATE INDEX IF NOT EXISTS idx_note_tags_note ON note_tags(note_id);
CREATE INDEX IF NOT EXISTS idx_note_tags_tag ON note_tags(tag_id);

-- Sync operation queue (replaces Dexie syncQueue)
CREATE TABLE IF NOT EXISTS sync_queue (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  type TEXT NOT NULL,
  entity TEXT NOT NULL,
  entity_id TEXT NOT NULL,
  data TEXT,
  timestamp INTEGER NOT NULL,
  retries INTEGER NOT NULL DEFAULT 0
);

-- Note links (wiki-style [[note]] references)
CREATE TABLE IF NOT EXISTS note_links (
  source_note_id TEXT NOT NULL,
  target_note_id TEXT NOT NULL,
  PRIMARY KEY (source_note_id, target_note_id)
);

CREATE INDEX IF NOT EXISTS idx_note_links_source ON note_links(source_note_id);
CREATE INDEX IF NOT EXISTS idx_note_links_target ON note_links(target_note_id);

-- Attachment projection (derived from markdown image + file-attachment syntax)
CREATE TABLE IF NOT EXISTS attachment_projection (
  note_id TEXT NOT NULL REFERENCES notes(id) ON DELETE CASCADE,
  position INTEGER NOT NULL DEFAULT 0,
  user_id TEXT NOT NULL DEFAULT '',
  kind TEXT NOT NULL DEFAULT 'image',
  alt TEXT NOT NULL DEFAULT '',
  src TEXT NOT NULL,
  created_at TEXT NOT NULL,
  updated_at TEXT NOT NULL,
  PRIMARY KEY (note_id, position)
);

CREATE INDEX IF NOT EXISTS idx_attachment_projection_note ON attachment_projection(note_id);
CREATE INDEX IF NOT EXISTS idx_attachment_projection_src ON attachment_projection(src);

-- Key-value metadata store
CREATE TABLE IF NOT EXISTS meta (
  key TEXT PRIMARY KEY,
  value TEXT NOT NULL
);

-- Schema version tracking
CREATE TABLE IF NOT EXISTS schema_version (
  version INTEGER PRIMARY KEY
);

-- Image upload queue
--
-- New rows use attachment_id + final_url: the client picks the attachment id
-- up front, the final URL is derived from it, and that URL is already in the
-- note's markdown when the row is enqueued. Retries upload under the same id
-- (idempotent server-side); no base64-src swap needed.
--
-- Legacy rows use base64_src: the old flow stored the data URL in markdown
-- and the retry drained it by substring-replacing that URL with whatever the
-- server eventually returned. Those rows still work for migration, but new
-- inserts should always supply attachment_id + final_url.
CREATE TABLE IF NOT EXISTS image_upload_queue (
  id INTEGER PRIMARY KEY AUTOINCREMENT,
  note_id TEXT NOT NULL,
  file_name TEXT NOT NULL,
  content_type TEXT NOT NULL,
  file_data TEXT NOT NULL,
  base64_src TEXT NOT NULL DEFAULT '',
  attachment_id TEXT NOT NULL DEFAULT '',
  final_url TEXT NOT NULL DEFAULT '',
  status TEXT NOT NULL DEFAULT 'pending',
  retries INTEGER NOT NULL DEFAULT 0,
  created_at TEXT NOT NULL DEFAULT (datetime('now'))
);

CREATE INDEX IF NOT EXISTS idx_image_queue_status ON image_upload_queue(status);
-- NO index on final_url here. It is a migrated column (v7 ALTER TABLE), and
-- SCHEMA_SQL runs BEFORE the ladder: on a DB last opened between v1 and v7 the
-- CREATE TABLE above is a no-op, the column does not exist yet, and indexing it
-- throws before migration 7 can add it. That kills startup permanently (nothing
-- catches it above the error screen). The index lives in migration 16, which a
-- fresh install runs too. Same trap as archived_at, see the note at the top.

-- Pointer rows for "Shared with me" — synced via the shared_note_ref entity.
-- A row means the user has note <note_id> in their inbox. The id is the
-- entityId on the worker side. Content is NOT stored here (see shared_notes
-- below) so this table stays small in the sync_queue payloads.
CREATE TABLE IF NOT EXISTS shared_note_refs (
  id TEXT PRIMARY KEY,
  user_id TEXT NOT NULL DEFAULT '',
  note_id TEXT NOT NULL,
  added_at TEXT NOT NULL DEFAULT (datetime('now')),
  sync_status TEXT NOT NULL DEFAULT 'synced'
);

CREATE INDEX IF NOT EXISTS idx_shared_note_refs_user_added ON shared_note_refs(user_id, added_at DESC);
CREATE INDEX IF NOT EXISTS idx_shared_note_refs_note ON shared_note_refs(note_id);
CREATE UNIQUE INDEX IF NOT EXISTS idx_shared_note_refs_user_note ON shared_note_refs(user_id, note_id);

-- Local content cache for shared notes — fetched from GET /public/share/:id.
-- Not synced. Refreshed on view; dropped when the corresponding pointer row
-- in shared_note_refs is removed (locally or via pull cascade).
CREATE TABLE IF NOT EXISTS shared_notes (
  id TEXT PRIMARY KEY,
  title TEXT NOT NULL DEFAULT '',
  content TEXT NOT NULL DEFAULT '',
  author_name TEXT NOT NULL DEFAULT '',
  created_at TEXT NOT NULL,
  updated_at TEXT NOT NULL,
  fetched_at TEXT NOT NULL,
  mentions_json TEXT
);

CREATE INDEX IF NOT EXISTS idx_shared_notes_fetched_at ON shared_notes(fetched_at DESC);

-- Local cache for the help center corpus — fetched from GET /public/help and
-- stored as one JSON blob (single row, id = 'current') so the Help dialog
-- renders instantly offline. Not synced; refreshed in the background on open.
CREATE TABLE IF NOT EXISTS help_content (
  id TEXT PRIMARY KEY CHECK (id = 'current'),
  locale TEXT NOT NULL,
  version TEXT NOT NULL,
  etag TEXT,
  payload_json TEXT NOT NULL,
  fetched_at TEXT NOT NULL
);
`;

const ENGINE_COLUMNS: Array<[string, string]> = [
  ["rev", "ALTER TABLE notes ADD COLUMN rev INTEGER NOT NULL DEFAULT 0"],
  ["base_updated_at", "ALTER TABLE notes ADD COLUMN base_updated_at TEXT NOT NULL DEFAULT ''"],
  ["inflight_client_id", "ALTER TABLE notes ADD COLUMN inflight_client_id TEXT"],
  ["inflight_seq", "ALTER TABLE notes ADD COLUMN inflight_seq INTEGER"],
  ["inflight_base_rev", "ALTER TABLE notes ADD COLUMN inflight_base_rev INTEGER"],
  ["inflight_ops", "ALTER TABLE notes ADD COLUMN inflight_ops TEXT"],
  ["pending_ops", "ALTER TABLE notes ADD COLUMN pending_ops TEXT"],
];

/** A shared note's who-is-whose fields (v19), set from pulled rows. */
const SHARING_COLUMNS: Array<[string, string]> = [
  ["owner_id", "ALTER TABLE notes ADD COLUMN owner_id TEXT"],
  ["owner_name", "ALTER TABLE notes ADD COLUMN owner_name TEXT"],
  ["share_role", "ALTER TABLE notes ADD COLUMN share_role TEXT"],
  ["member_count", "ALTER TABLE notes ADD COLUMN member_count INTEGER NOT NULL DEFAULT 0"],
  ["link_access", "ALTER TABLE notes ADD COLUMN link_access TEXT"],
];

const CURRENT_SCHEMA_VERSION = 19;

export function applySchema(db: Database): void {
  db.run(SCHEMA_SQL);

  // Check and apply migrations
  const versionResult = db.exec("SELECT MAX(version) as v FROM schema_version");
  const currentVersion =
    versionResult.length > 0 && versionResult[0].values.length > 0
      ? ((versionResult[0].values[0][0] as number | null) ?? 0)
      : 0;

  if (currentVersion < CURRENT_SCHEMA_VERSION) {
    if (currentVersion < 2) {
      // Remove share_token column — sharing now uses the note ID directly
      try {
        db.run('ALTER TABLE notes DROP COLUMN share_token');
      } catch {
        // Column may not exist (fresh install) — safe to ignore
      }
    }
    if (currentVersion < 4) {
      // Drop is_archived column — archive feature removed.
      // Guard with PRAGMA table_info so the migration is idempotent even
      // if the column was never created (fresh install on new schema).
      const info = db.exec("PRAGMA table_info(notes)");
      const hasIsArchived =
        info.length > 0 &&
        info[0].values.some((row) => row[1] === "is_archived");
      if (hasIsArchived) {
        try {
          db.run("UPDATE notes SET is_archived = 0 WHERE is_archived = 1");
        } catch {
          // Safe to ignore — column may be missing on exotic SQLite builds
        }
        try {
          db.run("ALTER TABLE notes DROP COLUMN is_archived");
        } catch {
          // sql.js should support DROP COLUMN, but guard anyway
        }
      }
    }
    if (currentVersion < 6) {
      db.run("DELETE FROM attachment_projection");
      const rows = db.exec(`
        SELECT id, user_id, content, created_at, updated_at
        FROM notes
      `);
      const values = rows[0]?.values ?? [];
      for (const value of values) {
        const [id, userId, content, createdAt, updatedAt] = value as [
          string,
          string,
          string,
          string,
          string,
        ];
        for (const attachment of projectNoteAttachments({
          id,
          userId,
          content,
          createdAt,
          updatedAt,
        })) {
          db.run(
            `INSERT OR REPLACE INTO attachment_projection
             (note_id, position, user_id, alt, src, created_at, updated_at)
             VALUES (?, ?, ?, ?, ?, ?, ?)`,
            [
              attachment.noteId,
              attachment.position,
              attachment.userId,
              attachment.alt,
              attachment.src,
              attachment.createdAt,
              attachment.updatedAt,
            ],
          );
        }
      }
    }
    if (currentVersion < 7) {
      // Add attachment_id + final_url columns to the image upload queue so
      // new rows can carry the client-supplied id that's baked into markdown.
      const info = db.exec("PRAGMA table_info(image_upload_queue)");
      const cols = new Set(
        (info[0]?.values ?? []).map((row) => row[1] as string),
      );
      if (!cols.has("attachment_id")) {
        db.run(
          "ALTER TABLE image_upload_queue ADD COLUMN attachment_id TEXT NOT NULL DEFAULT ''",
        );
      }
      if (!cols.has("final_url")) {
        db.run(
          "ALTER TABLE image_upload_queue ADD COLUMN final_url TEXT NOT NULL DEFAULT ''",
        );
      }
    }
    if (currentVersion < 8) {
      // shared_note_refs + shared_notes are created by the SCHEMA_SQL above
      // (CREATE IF NOT EXISTS), so the migration step is a no-op aside from
      // bumping the version number. Listed explicitly so future schema bumps
      // don't accidentally collide.
    }
    if (currentVersion < 9) {
      // Force one full sync re-pull to recover from the cross-device
      // watermark bug. Older builds advanced the local watermark from the
      // `/sync/push` response's `serverVersion` — which is the user's
      // *global* counter, including entries from other devices we hadn't
      // pulled yet. So pushing while another device had pending sync_log
      // entries (a remote `note_tag` create from another client, for
      // example) caused those entries to be permanently skipped on the
      // next pull (`sinceVersion = watermark`). Symptom: tags appear in
      // the sidebar but the note doesn't appear in the tag's note list
      // until the user edits the note, which re-extracts hashtags
      // locally. Clearing the watermark lets the next pull replay every
      // sync_log entry and re-apply the missed `note_tag` rows. Mirrors
      // the Swift v12 migration. Idempotent for users who never hit it.
      db.run(
        "DELETE FROM meta WHERE key IN ('syncServerVersion', 'lastSyncedAt')",
      );
    }
    if (currentVersion < 10) {
      // Add a `kind` column ('image' | 'file') to the attachment projection so
      // the note list can render image thumbnails vs file chips. CREATE TABLE
      // IF NOT EXISTS never alters an existing table, so add the column
      // explicitly (guarded so it's idempotent), then rebuild the projection
      // from note content to backfill the correct kind.
      const info = db.exec("PRAGMA table_info(attachment_projection)");
      const cols = new Set((info[0]?.values ?? []).map((row) => row[1] as string));
      if (!cols.has("kind")) {
        db.run(
          "ALTER TABLE attachment_projection ADD COLUMN kind TEXT NOT NULL DEFAULT 'image'",
        );
      }
      db.run("DELETE FROM attachment_projection");
      const rows = db.exec(`
        SELECT id, user_id, content, created_at, updated_at
        FROM notes
      `);
      const values = rows[0]?.values ?? [];
      for (const value of values) {
        const [id, userId, content, createdAt, updatedAt] = value as [
          string,
          string,
          string,
          string,
          string,
        ];
        for (const attachment of projectNoteAttachments({
          id,
          userId,
          content,
          createdAt,
          updatedAt,
        })) {
          db.run(
            `INSERT OR REPLACE INTO attachment_projection
             (note_id, position, user_id, kind, alt, src, created_at, updated_at)
             VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
            [
              attachment.noteId,
              attachment.position,
              attachment.userId,
              attachment.kind,
              attachment.alt,
              attachment.src,
              attachment.createdAt,
              attachment.updatedAt,
            ],
          );
        }
      }
    }
    if (currentVersion < 11) {
      // Add archived_at column for the archive feature. Archived notes are
      // hidden like trash but never auto-removed and keep their tags. Guard
      // with PRAGMA table_info so the migration is idempotent (fresh installs
      // already have the column from SCHEMA_SQL above).
      const info = db.exec("PRAGMA table_info(notes)");
      const hasArchivedAt =
        info.length > 0 &&
        info[0].values.some((row) => row[1] === "archived_at");
      if (!hasArchivedAt) {
        db.run("ALTER TABLE notes ADD COLUMN archived_at TEXT");
      }
      db.run(
        "CREATE INDEX IF NOT EXISTS idx_notes_archived_at ON notes(archived_at)",
      );
    }
    if (currentVersion < 12) {
      // Add is_pinned to tags — pinned tags sort first in the sidebar.
      // Guard with PRAGMA table_info so the migration is idempotent (fresh
      // installs already have the column from SCHEMA_SQL above).
      const info = db.exec("PRAGMA table_info(tags)");
      const hasIsPinned =
        info.length > 0 &&
        info[0].values.some((row) => row[1] === "is_pinned");
      if (!hasIsPinned) {
        db.run(
          "ALTER TABLE tags ADD COLUMN is_pinned INTEGER NOT NULL DEFAULT 0",
        );
      }
    }
    if (currentVersion < 13) {
      // Add sort_order to notes — the manual-order key for the "Manual" sort
      // mode (epoch-ms scale, NULL falls back to created_at). No backfill, no
      // index (clients sort locally). Guard with PRAGMA table_info so the
      // migration is idempotent (fresh installs already have it from SCHEMA_SQL).
      const info = db.exec("PRAGMA table_info(notes)");
      const hasSortOrder =
        info.length > 0 &&
        info[0].values.some((row) => row[1] === "sort_order");
      if (!hasSortOrder) {
        db.run("ALTER TABLE notes ADD COLUMN sort_order REAL");
      }
    }
    if (currentVersion < 14) {
      // Add base_content to notes — the last-synced content, used as the merge
      // ancestor for git-style three-way conflict resolution. No backfill (NULL
      // falls back to pick-a-side); it populates on the note's next sync. No
      // index. Guard with PRAGMA table_info so the migration is idempotent.
      const info = db.exec("PRAGMA table_info(notes)");
      const hasBaseContent =
        info.length > 0 &&
        info[0].values.some((row) => row[1] === "base_content");
      if (!hasBaseContent) {
        db.run("ALTER TABLE notes ADD COLUMN base_content TEXT");
      }
    }
    if (currentVersion < 15) {
      // Add is_read_only to notes — the "Make Read-Only" editing guard. No
      // backfill (0 = editable, the pre-existing behaviour) and no index
      // (nothing filters or sorts by it). Guard with PRAGMA table_info so the
      // migration is idempotent (fresh installs already have it from SCHEMA_SQL).
      const info = db.exec("PRAGMA table_info(notes)");
      const hasIsReadOnly =
        info.length > 0 &&
        info[0].values.some((row) => row[1] === "is_read_only");
      if (!hasIsReadOnly) {
        db.run(
          "ALTER TABLE notes ADD COLUMN is_read_only INTEGER NOT NULL DEFAULT 0",
        );
      }
    }
    if (currentVersion < 16) {
      // Index image_upload_queue by final_url. Without an account the queue row
      // is the ONLY durable copy of an attachment, and the render path looks
      // bytes up by the URL sitting in the markdown (`queuedAttachmentBytes`),
      // once per URL, on every reload. CREATE INDEX IF NOT EXISTS is idempotent
      // and the column has existed since v7, so this needs no PRAGMA guard.
      db.run(
        "CREATE INDEX IF NOT EXISTS idx_image_queue_final_url ON image_upload_queue(final_url)",
      );
    }
    if (currentVersion < 17) {
      // Reminders left the web app. The projection was derived from note
      // frontmatter; the `reminders:` blocks themselves stay in the notes,
      // untouched, for older clients. Dropping the table drops its indexes.
      db.run("DROP TABLE IF EXISTS reminder_projection");
    }
    if (currentVersion < 18) {
      // The content sync engine's per-note state (PLAN.md "Client engine"):
      // the server rev `base_content` is at (0 = unknown), its timestamp (for
      // falling back to legacy), the change in flight and the buffer. All
      // NULL / 0 until the account switches to v2 sync; no backfill, no index.
      for (const [column, ddl] of ENGINE_COLUMNS) ensureColumn(db, "notes", column, ddl);
    }
    if (currentVersion < 19) {
      // Shared notes (PLAN.md "Sharing data"): whose the note is and the
      // person's role, from pulled rows. NULL / 0 for every existing note
      // (the level 3 re-pull fills them); no index.
      for (const [column, ddl] of SHARING_COLUMNS) ensureColumn(db, "notes", column, ddl);
    }
    db.run(
      `INSERT OR REPLACE INTO schema_version (version) VALUES (${CURRENT_SCHEMA_VERSION})`,
    );
  }

  // Self-heal schema drift. The version-gated migrations above are skipped once
  // schema_version reaches CURRENT_SCHEMA_VERSION, so a DB that landed on a high
  // version WITHOUT a column (e.g. the version was bumped for an unrelated
  // migration on another branch during dev/prerelease) never gets the ALTER and
  // then throws "no such column" at query time. These PRAGMA-guarded adds are
  // cheap and idempotent, so run them unconditionally on every startup.
  ensureColumn(db, "notes", "archived_at", "ALTER TABLE notes ADD COLUMN archived_at TEXT");
  ensureColumn(db, "notes", "sort_order", "ALTER TABLE notes ADD COLUMN sort_order REAL");
  ensureColumn(db, "notes", "base_content", "ALTER TABLE notes ADD COLUMN base_content TEXT");
  ensureColumn(db, "notes", "is_read_only", "ALTER TABLE notes ADD COLUMN is_read_only INTEGER NOT NULL DEFAULT 0");
  for (const [column, ddl] of ENGINE_COLUMNS) ensureColumn(db, "notes", column, ddl);
  for (const [column, ddl] of SHARING_COLUMNS) ensureColumn(db, "notes", column, ddl);
  ensureColumn(db, "tags", "is_pinned", "ALTER TABLE tags ADD COLUMN is_pinned INTEGER NOT NULL DEFAULT 0");
}

/** Adds a column via `ddl` only if it's missing. Idempotent. */
function ensureColumn(
  db: Database,
  table: string,
  column: string,
  ddl: string,
): void {
  const info = db.exec(`PRAGMA table_info(${table})`);
  const has =
    info.length > 0 && info[0].values.some((row) => row[1] === column);
  if (!has) db.run(ddl);
}

// ── Initialization ─────────────────────────────────────────────────────────

export async function initSqlite(): Promise<Database> {
  if (_db) return _db;
  if (_initPromise) return _initPromise;

  _initPromise = (async () => {
    // Load sql.js WASM
    const SQL = await initSqlJs({
      locateFile: () => sqlWasmUrl,
    });

    // Try to load existing database from IndexedDB
    const saved = await loadFromIDB();
    if (saved) {
      _db = new SQL.Database(saved);
    } else {
      _db = new SQL.Database();
    }

    // Enable WAL mode and foreign keys
    _db.run("PRAGMA journal_mode = WAL");
    _db.run("PRAGMA foreign_keys = ON");

    // Apply schema (idempotent — uses IF NOT EXISTS)
    applySchema(_db);
    registerSearchFunctions(_db);

    // Persist on page unload
    window.addEventListener("beforeunload", () => {
      if (_db) {
        const data = exportDatabase(_db);
        // Use synchronous IndexedDB write via hidden API or fall back
        // In practice, the debounced persist should have already saved
        const idbReq = indexedDB.open(IDB_NAME, 1);
        idbReq.onsuccess = () => {
          const tx = idbReq.result.transaction(IDB_STORE, "readwrite");
          tx.objectStore(IDB_STORE).put(data, IDB_KEY);
        };
      }
    });

    // Notify all subscribers that the DB is ready — this triggers
    // useSQLiteQuery hooks that mounted before init completed.
    notifyChanges([
      "notes",
      "tags",
      "note_tags",
      "note_links",
      "attachment_projection",
      "shared_note_refs",
      "shared_notes",
      "sync_queue",
      "meta",
    ]);

    return _db;
  })();

  return _initPromise;
}

/** Get the database instance (must call initSqlite() first). */
export function getDb(): Database {
  if (!_db) throw new Error("SQLite not initialized — call initSqlite() first");
  return _db;
}

/** Check if the database has been initialized. */
export function isDbReady(): boolean {
  return _db !== null;
}

// ── Query Helpers ──────────────────────────────────────────────────────────

export interface QueryResult<T = Record<string, unknown>> {
  rows: T[];
}

/** Run a SELECT query and return typed rows. */
export function query<T = Record<string, unknown>>(
  sql: string,
  params?: unknown[],
): T[] {
  const db = getDb();
  const stmt = db.prepare(sql);
  if (params) stmt.bind(params as (string | number | null | Uint8Array)[]);

  const rows: T[] = [];
  while (stmt.step()) {
    rows.push(stmt.getAsObject() as T);
  }
  stmt.free();
  return rows;
}

/** Run a single-row SELECT query. */
export function queryOne<T = Record<string, unknown>>(
  sql: string,
  params?: unknown[],
): T | null {
  const rows = query<T>(sql, params);
  return rows[0] ?? null;
}

/** Run an INSERT/UPDATE/DELETE statement. */
export function execute(sql: string, params?: unknown[]): void {
  const db = getDb();
  db.run(sql, params as (string | number | null | Uint8Array)[] | undefined);
  schedulePersist();
}

/** Track transaction nesting depth for SAVEPOINT support. */
let _txDepth = 0;

/** Run multiple statements in a transaction. Supports nesting via SAVEPOINTs. */
export function transaction(fn: () => void): void {
  const db = getDb();
  const nested = _txDepth > 0;
  const savepointName = `sp_${_txDepth}`;

  if (nested) {
    db.run(`SAVEPOINT ${savepointName}`);
  } else {
    db.run("BEGIN TRANSACTION");
  }
  _txDepth++;

  try {
    fn();
    _txDepth--;
    if (nested) {
      db.run(`RELEASE ${savepointName}`);
    } else {
      db.run("COMMIT");
      schedulePersist();
    }
  } catch (e) {
    _txDepth--;
    if (nested) {
      db.run(`ROLLBACK TO ${savepointName}`);
      db.run(`RELEASE ${savepointName}`);
    } else {
      db.run("ROLLBACK");
    }
    throw e;
  }
}

/** Run multiple statements in a transaction and notify changes. */
export function transactionWithNotify(tables: string[], fn: () => void): void {
  transaction(fn);
  notifyChanges(tables);
}

/** Wipe all data — used on logout. */
export function clearAllData(): void {
  const db = getDb();
  db.run("BEGIN TRANSACTION");
  try {
    db.run("DELETE FROM note_tags");
    db.run("DELETE FROM note_links");
    db.run("DELETE FROM attachment_projection");
    db.run("DELETE FROM shared_note_refs");
    db.run("DELETE FROM shared_notes");
    db.run("DELETE FROM sync_queue");
    // The image queue holds raw attachment bytes. Left behind, the first sync
    // cycle after signing into a DIFFERENT account would upload the previous
    // user's images into it.
    db.run("DELETE FROM image_upload_queue");
    db.run("DELETE FROM notes");
    db.run("DELETE FROM tags");
    db.run("DELETE FROM meta");
    db.run("COMMIT");
  } catch (e) {
    db.run("ROLLBACK");
    throw e;
  }
  schedulePersist();
  notifyChanges([
    "notes",
    "tags",
    "note_tags",
    "note_links",
    "attachment_projection",
    "shared_note_refs",
    "shared_notes",
    "sync_queue",
    "meta",
  ]);
}
